import { inject, Injectable } from '@angular/core';
import {
  collectionGroup,
  collectionSnapshots,
  documentId,
  getAggregateFromServer,
  query,
  sum,
  where,
  Firestore,
  DocumentReference,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
  onSnapshot,
} from '@angular/fire/firestore';
import { GameStartedResponse } from '@core/models/game/game-started-response.interface';
import {
  ChangedQuestionsInterface,
  DateRange,
  GameNoteInterface,
  GameStarted,
  GameState,
  Success,
  Team,
  TeamAnswer,
  TeamInfo,
  TriviaGame,
} from '@models';
import { catchError, forkJoin, from, map, Observable, of } from 'rxjs';
import * as _ from 'lodash';
import { Moment } from 'moment';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private firestore = inject(Firestore);

  getAllTeams(
    code: string,
  ): Observable<{ ref: DocumentReference<Team>; team: Team }[]> {
    code = code.toString();
    const teamsCollection = collection(
      this.firestore,
      'games',
      code,
      'teams',
    ) as CollectionReference<Team>;
    return from(getDocs(teamsCollection)).pipe(
      map((snapshot) =>
        snapshot.docs.map((t) => {
          return { ref: t.ref, team: t.data() };
        }),
      ),
    );
  }

  finishGame(
    event_id: string,
    code: string,
    host_id: string,
    location_id: string,
    player_count: number,
    team_count: number,
  ) {
    code = code.toString();
    const eventInfoDoc = doc(this.firestore, 'event_info', event_id);

    const updatePlayerNumbers = from(
      setDoc(
        eventInfoDoc,
        {
          player_count: player_count,
          team_count: team_count,
        },
        { merge: true },
      ),
    );
    const teamsCollection = collection(this.firestore, 'games', code, 'teams');

    const updateTeams = from(getDocs(teamsCollection)).pipe(
      map((snapshot) =>
        snapshot.docs.forEach((d) => {
          updateDoc(d.ref, {
            completed: true,
            host_id: host_id,
            location_id: location_id,
            date: new Date(),
          });
        }),
      ),
    );

    return forkJoin([updatePlayerNumbers, updateTeams]);
  }

  sortTeams(
    teams: {
      ref: DocumentReference<Team>;
      team: Team;
    }[],
    batch: any,
  ) {
    const scores = teams
      .sort((a, b) => b.team.score - a.team.score)
      .map((t) => t.team.score);
    let rank = 1;

    // update rank so teams with same score receive the same rank
    teams.forEach((t, i) => {
      const { score } = t.team;
      if (i > 0 && score < scores[i - 1]) {
        rank = i + 1;
      }
      t.team.rank = rank;
    });

    teams.forEach((t) => batch.update(t.ref, t.team));
  }

  getGame(code: string) {
    const gameDoc = doc(this.firestore, 'games', code);

    return from(getDoc(gameDoc)).pipe(
      map((docSnapshot) => docSnapshot.data() as TriviaGame),
      catchError((err) => {
        console.log(err);
        return of(null);
      }),
    );
  }

  getEventsInfo(event_ids: string[]) {
    const collectionRef = collection(this.firestore, 'event_info');
    const eventQuery = query(
      collectionRef,
      where(documentId(), 'in', event_ids),
    );

    return from(getDocs(eventQuery)).pipe(
      map((snapshot) =>
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      ),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
    );
  }

  createGame(
    triviaGame: TriviaGame,
    code: string,
  ): Observable<TriviaGame | null> {
    const docRef = doc(collection(this.firestore, 'games'), code);

    return from(setDoc(docRef, triviaGame)).pipe(
      map(() => triviaGame),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
    );
  }

  setGameBetaPart(
    isBeta: boolean,
    id: string,
  ): Observable<{ isBeta: boolean } | null> {
    const docRef = doc(collection(this.firestore, 'app_version'), id);

    return from(setDoc(docRef, { isBeta })).pipe(
      map(() => {
        return { isBeta };
      }),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
    );
  }

  onGameNoteChange(
    gameNotes: GameNoteInterface[],
    code: string,
  ): Observable<GameNoteInterface[] | null> {
    const docRef = doc(this.firestore, 'games', code, 'notes', 'gameNotes');

    return from(setDoc(docRef, { gameNotes }, { merge: true })).pipe(
      map(() => gameNotes),
      catchError((err) => {
        console.error('Firestore error:', err);
        return of(null);
      }),
    );
  }

  getGameNote(code: string): Observable<any> {
    if (!code) return of(null);

    const docRef = doc(
      this.firestore,
      'games',
      code.toString(),
      'notes',
      'gameNotes',
    );

    return from(getDoc(docRef)).pipe(
      map((docSnap) => {
        return docSnap.exists() ? docSnap.data() : null;
      }),
      catchError((err) => {
        console.error('Firestore error:', err);
        return of(null);
      }),
    );
  }

  async deleteGameFromFirebase(code: number, eventId: string): Promise<void> {
    const promises: Promise<void>[] = [];

    if (code) {
      const gameDocRef = doc(this.firestore, 'games', code.toString());
      promises.push(deleteDoc(gameDocRef));
    }

    if (eventId) {
      const versionDocRef = doc(
        this.firestore,
        'app_version',
        eventId.toString(),
      );
      promises.push(deleteDoc(versionDocRef));
    }

    try {
      await Promise.all(promises);
    } catch (err) {
      console.error('Error deleting documents:', err);
    }
  }
  getAcceptedQuestions(
    round_id: string,
    code: string,
  ): Observable<ChangedQuestionsInterface> {
    const subcollectionRef = collection(
      this.firestore,
      'games',
      code,
      'correct_answers',
    );
    const q = query(subcollectionRef, where('round_id', '==', round_id));

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        const res: ChangedQuestionsInterface = {};
        snapshot.docs.forEach((docSnap) => {
          const obj = docSnap.data();
          _.set(
            res,
            `[${obj.round_id}][${obj.question_id}].answers`,
            obj.correct_answers ?? [],
          );
        });
        return res;
      }),
    );
  }

  getRoundAnswers(round_id: string, code: string): Observable<TeamAnswer[]> {
    const subcollectionRef = collection(
      this.firestore,
      'games',
      code,
      'answers',
    );
    const q = query(subcollectionRef, where('round_id', '==', round_id));

    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map((docSnap) => {
          const teamAnswer: TeamAnswer = {
            ...(docSnap.data() as TeamAnswer),
            answer_id: docSnap.id,
          };
          return teamAnswer;
        }),
      ),
    );
  }

  startGame(data: GameStarted): Observable<GameStartedResponse | null> {
    const initialState: GameState = {
      questionTimer: [],
      acceptedQuestionAnswerIds: [],
      completedRoundsCount: 0,
      splashScreenIndex: 0,
      selectedTabIndex: 0,
      activeQuestionAnswerIndex: 0,
      questionTimeIsSet: false,
      is_started: true,
      gameScreen: '',
      activeEventId: '',
      teamsAccepted: false,
      scoringRoundsCount: 0,
    };

    const docRef = doc(this.firestore, 'games', data.game_code);

    // 1. updateDoc cleanly updates multiple separate nested paths at once
    return from(
      updateDoc(docRef, {
        'trivia_game.start_datetime': data.start_datetime,
        'trivia_game.state': initialState,
      }),
    ).pipe(
      map(() => {
        return {} as GameStartedResponse;
      }),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
    );
  }

  getLeaderboard(code: string): Observable<any[]> {
    code = String(code);
    const subcollectionRef = collection(this.firestore, 'games', code, 'teams');
    const q = query(subcollectionRef);

    return new Observable((subscriber) => {
      const unsubscribe = onSnapshot(q, {
        next: (snap) => {
          const items = snap.docs.map((doc) => ({
            ...(doc.data() as Team),
            team_id: doc.id,
          }));
          subscriber.next(items);
        },
        error: (err) => subscriber.error(err),
      });

      return () => unsubscribe();
    });
  }

  getHostStats(
    host_id: string,
    range: DateRange,
  ): Observable<{ total_players: number }> {
    const c = collectionGroup(this.firestore, 'teams');

    const host_query = where('host_id', '==', host_id);
    const start_query = where(
      'date',
      '>',
      (range.start as Moment).toISOString(),
    );
    const end_query = where('date', '<', (range.end as Moment).toISOString());

    const q = query(c, host_query, start_query, end_query);

    return from(
      getAggregateFromServer(q, {
        total_players: sum('player_numbers_survey'),
      }),
    ).pipe(map((a) => a.data() as { total_players: number }));
  }

  getTeamMembers(teamId: string, code: string): Observable<TeamInfo | null> {
    code = String(code);
    const docRef = doc(this.firestore, 'games', code, 'teams', teamId);

    return from(getDoc(docRef)).pipe(
      map((docSnapshot) => {
        return docSnapshot.exists() ? (docSnapshot.data() as TeamInfo) : null;
      }),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
    );
  }

  getTeamAnswers(
    round_id: string,
    team_id: string,
    gameCode: string,
  ): Observable<TeamAnswer[]> {
    gameCode = String(gameCode);
    const subcollectionRef = collection(
      this.firestore,
      'games',
      gameCode,
      'answers',
    );
    const q = query(
      subcollectionRef,
      where('round_id', '==', round_id),
      where('team_id', '==', team_id),
    );

    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map((docSnap) => docSnap.data() as TeamAnswer),
      ),
    );
  }

  changedLeaderboardTeam(teamData: Team[], code: string): Observable<void> {
    const batch = writeBatch(this.firestore);
    const teams = teamData.map((t) => {
      const teamDocRef = doc(
        this.firestore,
        'games',
        code.toString(),
        'teams',
        t.team_id,
      ) as DocumentReference<Team>;

      return {
        team: {
          ...t,
          bonus: t.bonus + (+t.bonus_view || 0),
          score: t.score + (+t.bonus_view || 0),
          bonus_added: (+t.bonus_view || 0) + (+t.bonus || 0),
        } as Team,
        ref: teamDocRef,
      };
    });

    this.sortTeams(teams, batch);

    return from(batch.commit());
  }

  saveGameState(data: GameState, code: string): Observable<Success | null> {
    if (!code) return of(null);
    code = code.toString();

    const docRef = doc(this.firestore, 'games', code);

    return from(updateDoc(docRef, { 'trivia_game.state': data })).pipe(
      map(() => {
        return { msg: 'updated' } as Success;
      }),
      catchError((err) => {
        console.error(err);
        return of({ msg: err } as Success);
      }),
    );
  }

  getGameState(code: string): Observable<GameState | null> {
    if (!code) return of(null);
    code = code.toString();

    const docRef = doc(this.firestore, 'games', code);

    return from(getDoc(docRef)).pipe(
      map((docSnap) => {
        if (docSnap.exists()) {
          const gameData = docSnap.data();
          return gameData?.['trivia_game']?.['state'] as GameState;
        }
        return null;
      }),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
    );
  }
}
